'use client'
import React, { useCallback, useEffect, useState } from 'react'
import { t } from 'i18next'
import Header from 'app/app/(client)/components/templates/Header/Header'
import Toast from 'app/app/(client)/components/organisms/Toast/Toast'
import AlertBox from 'app/app/(client)/components/organisms/AlertBox/AlertBox'
import { Constants } from './utils/client-constants'
import { usePathname } from 'next/navigation'
import * as localeHelper from 'app/app/common/locale'
import { useAppDispatch, useAppSelector } from './hooks/redux-hook'
import { clearSfCache, updateUserSettings } from 'app/services/client/client-api'
import * as R from 'ramda'
import { resetOpportunity } from './redux/features/currentOffer/reset-opportunity'
import { capitalizeIfEnglish } from './utils/utils'
import { languageChangeModalProp, logoutAlertProps } from './redux/features/notification/modal-state'
import RadioButtonGroup from './components/molecules/RadioButtonGroup/RadioButtonGroup'
import { syncOfferAsync } from './redux/features/sync/sync-offer-async'
import { useNetwork } from './hooks/useNetwork'
import { useError } from './hooks/useError'
import { handleUIError } from 'app/services/client/error-handling'
import { getUnhandledError } from 'app/services/common-error-utils'
import { useAlert } from './hooks/useAlert'
import { useNavigate } from './hooks/useNavigate'

const urlPattern: RegExp = /^\/[a-zA-Z0-9_-]+\/prepare(\/[a-zA-Z0-9_.-]+)*\/?$/

const ClientRootLayout = ({ children }: { children: React.ReactNode }) => {
  const router = useNavigate()
  const dispatch = useAppDispatch()
  const pathName = usePathname()
  const alert = useAlert()
  const { dismissGlobalError, globalErrorInfo } = useError()
  const [nativeByLocale, setNativeByLocale] = useState<{ [key: string]: string }>({})
  const currentOffer = useAppSelector(state => state.currentOffer).data
  const unSavedOfferCount = useAppSelector(state => state.unSavedOfferCount).data || 0
  const isOnline = useNetwork()
  const appConfigState = useAppSelector(state => state.configdata)
  const { data: isAppInitialized } = useAppSelector(state => state.appInitialized) || { data: false }

  const configDataState = useAppSelector(state => state.configdata) || {
    status: 'idle'
  }

  const customerDataState = useAppSelector(state => state.customers) || {
    status: 'idle'
  }

  const { frontLineConfig, settings, profile } = configDataState.data || {}
  const [selectedLanguage, setSelectedLanguage] = useState('English')
  const [selectedLocal, setSelectedLocal] = useState('en-US')
  const [userName, setUserName] = useState('--')
  const [isLogout, setIsLogout] = useState(false)
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const [loadingPrimaryButton, setLoadingPrimaryButton] = useState(false)
  const [loadingLogoutProps, setLoadingLogoutProps] = useState<Record<string, any>>({
    isLoading: false,
    message: t('loggedOut.clear')
  })
  const [showRefreshButton, setShowRefreshButton] = useState(false)

  const navlinks = [
    {
      name: capitalizeIfEnglish(t('navigation.refresh.home')),
      key: 'home',
      link: Constants.HEADER_HOME_LINK,
      active: pathName === '/'
    }
  ]

  const handleLogoutClick = useCallback(() => {
    const onLogOut = () => {
      setLoadingLogoutProps({ isLoading: true, message: t('loggedout.logginOutPleaseWait') })
      alert.hide()
      setIsLogout(true)
      router.push('/logout')
      setLoadingLogoutProps(prev => ({ ...prev, isLoading: false }))
    }
    const { isLoading, message: description } = loadingLogoutProps
    alert.custom(logoutAlertProps(isLoading, description, onLogOut))
  }, [alert, router, loadingLogoutProps])

  useEffect(() => {
    if (globalErrorInfo.isVisible && globalErrorInfo.message) {
      handleUIError(dispatch, getUnhandledError(globalErrorInfo.message))
    }
  }, [globalErrorInfo.isVisible, globalErrorInfo.message, dismissGlobalError, dispatch, alert])

  useEffect(() => {
    const nativeLangObj = localeHelper.nativeLanguageByLocale(frontLineConfig?.languages || [])
    setUserName(profile?.displayName || '-')
    setNativeByLocale(nativeLangObj)
  }, [frontLineConfig, profile?.displayName])

  useEffect(() => {
    const currentlang: string = nativeByLocale[settings?.language as string] || '-'
    setSelectedLanguage(currentlang)
    setSelectedLocal(settings?.language as string)
  }, [settings, nativeByLocale])

  const onCloseLanguageModal = () => {
    setIsLanguageModalOpen(false)
    setLoadingPrimaryButton(false)
  }

  const onRefresh = async () => {
    if (pathName === '/') {
      await clearSfCache()
        .then(() => window.location.reload())
        .catch(e => alert.error(e))
      return
    }
    if (urlPattern.test(pathName)) {
      const opportunityId = currentOffer ? currentOffer.opportunity.id : ''
      if (!R.isEmpty(opportunityId)) {
        dispatch(resetOpportunity(opportunityId))
      }
    }
  }

  const onSync = () => {
    if (isOnline && unSavedOfferCount > 0) {
      dispatch(syncOfferAsync())
    }
  }

  useEffect(() => {
    setShowRefreshButton(pathName === '/' || urlPattern.test(pathName))
  }, [pathName])

  useEffect(() => {
    if (isLanguageModalOpen && Object.keys(nativeByLocale).length > 0) {
      const languageOptions = Object.entries(nativeByLocale).map(([locale, nativeName]) => ({
        value: locale,
        label: nativeName
      }))

      const onPrimaryClick = () => {
        if (settings?.language === selectedLocal) {
          setIsLanguageModalOpen(false)
          alert.hide()
          return
        }
        setLoadingPrimaryButton(true)
        updateUserSettings(selectedLocal)
          .then(() => {
            window.location.reload()
          })
          .catch(error => {
            alert.error(error)
          })
          .finally(() => {
            setIsLanguageModalOpen(false)
            setLoadingPrimaryButton(false)
            alert.hide()
          })
      }

      const modalChildren = (
        <div className="flex flex-col gap-4">
          <RadioButtonGroup
            options={languageOptions.map(opt => opt.label)}
            value={nativeByLocale[selectedLocal] || ''}
            onChange={value => {
              const selectedOption = languageOptions.find(opt => opt.label === value)
              if (selectedOption) {
                setSelectedLocal(selectedOption.value)
              }
            }}
            name="language-selection"
            className="flex flex-col gap-2"
            radioSize="medium"
          />
        </div>
      )
      alert.custom(languageChangeModalProp(modalChildren, onPrimaryClick, loadingPrimaryButton, onCloseLanguageModal))
    }
  }, [nativeByLocale, selectedLocal, isLanguageModalOpen, loadingPrimaryButton, dispatch, settings?.language, alert])

  if (!appConfigState.data) {
    return null
  }

  return (
    <>
      {!isLogout && isAppInitialized && customerDataState.status === 'succeeded' ? (
        <Header
          id="kcc-refresh-header"
          className="bg-white shadow-md sticky top-0 z-50"
          navlinks={navlinks}
          onClickHandlers={{
            onLogoutClick: handleLogoutClick,
            onLanguageChange: () => setIsLanguageModalOpen(true),
            onRefreshClick: () => onRefresh(),
            onSyncClick: () => onSync()
          }}
          selectedLanguage={selectedLanguage}
          userName={userName}
          showRefreshButton={showRefreshButton}
        />
      ) : null}
      {children}
      <AlertBox />
      <Toast />
    </>
  )
}

export default ClientRootLayout
